var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/result.ts
var WrappedResultError = class extends Error {
  constructor(message, originalError) {
    super(message);
    this.originalError = originalError;
  }
};
function valueIsError(value) {
  return value.type === "error";
}
function valueIsOk(value) {
  return value.type === "value";
}
var Result = class _Result {
  constructor(value) {
    this.value = value;
  }
  /**
   * Gets the underlying value. Note that you should check whether the value is Ok before doing this, or else a @see WrappedResultError may be thrown.
   */
  getValue() {
    if (valueIsError(this.value)) {
      throw new WrappedResultError(
        `Attempted to get the raw value of a Result, but the value was an Error.`,
        this.value.error
      );
    }
    return this.value.value;
  }
  /**
   * Gets the underlying error. Note that you should check whether the value is Error before doing this, or else a @see WrappedResultError may be thrown.
   */
  getError() {
    if (!valueIsError(this.value)) {
      const type = typeof this.value.value;
      throw new Error(
        `Attempted to get the Error of a Result, but the value was not an Error. Value type was ${type}.`
      );
    }
    return this.value.error;
  }
  /**
   * Checks whether the value is Error.
   */
  isError() {
    return valueIsError(this.value);
  }
  /**
   * Checks whether the value is Ok.
   */
  isOk() {
    return !this.isError();
  }
  /**
   * Maps the Ok value to another value. Will only run if the value is Ok.
   */
  map(mapper) {
    if (valueIsOk(this.value)) {
      return _Result.ofValue(mapper(this.value.value));
    }
    return _Result.ofError(this.value.error);
  }
  /**
   * Maps the Error value back to an OK value. Will only run if the value is Error.
   */
  mapError(mapper) {
    if (valueIsError(this.value)) {
      return _Result.ofValue(mapper(this.value.error));
    }
    return this;
  }
  /**
   * Binds the value to the result returned by the @param mapper function. Will only run if the value is Ok.
   */
  bind(mapper) {
    if (valueIsOk(this.value)) {
      return mapper(this.value.value);
    }
    return _Result.ofError(this.value.error);
  }
  /**
   * Binds the result to the one returned by the @param mapper function. Will only run if the value is Error.
   */
  bindError(mapper) {
    if (valueIsError(this.value)) {
      return mapper(this.value.error);
    }
    return _Result.ofValue(this.value.value);
  }
  /**
   * Passes the value to the given @param fn function where it can then be used for work that doesn't return a value. Will only run if the value is Ok.
   */
  iter(fn) {
    if (valueIsOk(this.value)) {
      fn(this.value.value);
    }
    return this;
  }
  /**
   * Executes the given @param fn function when the result is Error.
   */
  iterError(fn) {
    if (valueIsError(this.value)) {
      fn(this.value.error);
    }
    return this;
  }
  /**
   * Returns the @param defaultValue if the Result is Error, else returns the Ok value.
   * @example
   * console.log(Result.ofValue(5).defaultValue(10)) // 5
   * console.log(Result.ofError(new Error("Test error")).defaultValue(10)) // 10
   */
  defaultValue(defaultValue) {
    if (valueIsOk(this.value)) {
      return this.value.value;
    }
    return defaultValue;
  }
  /**
   * Wraps the @param value in an Ok Result.
   */
  static ofValue(value) {
    return new _Result({
      type: "value",
      value
    });
  }
  /**
   * Wraps the @param error in an Error Result.
   */
  static ofError(error) {
    return new _Result({
      type: "error",
      error
    });
  }
  /**
   * Takes the given promise or function that returns a promise and wraps it in a try/catch. Returns Result.ofError if it throws an error.
   */
  static ofPromise(promise) {
    const computation = typeof promise === "function" ? promise() : promise;
    return computation.then((x) => {
      const value = {
        type: "value",
        value: x
      };
      return value;
    }).catch((e) => {
      const error = {
        type: "error",
        error: e
      };
      return error;
    }).then((r) => valueIsError(r) ? _Result.ofError(r.error) : _Result.ofValue(r.value));
  }
  /**
   * Takes the given function and executes it, returning Result.ofError if it throws an error.
   */
  static ofFunction(computation) {
    try {
      return _Result.ofValue(computation());
    } catch (e) {
      return _Result.ofError(e);
    }
  }
  /**
   * Gets the underlying value of the Result. Note that you should check whether the value is Ok before doing this, or else a @see WrappedResultError may be thrown.
   */
  static getValue(result) {
    return result.getValue();
  }
  /**
   * Gets the underlying error of the Result. Note that you should check whether the value is Error before doing this, or else a @see WrappedResultError may be thrown.
   */
  static getError(result) {
    return result.getError();
  }
  /**
   * Checks whether the Result is Error.
   */
  static isError(result) {
    return result.isError();
  }
  /**
   * Checks whether the Result is Ok.
   */
  static isOk(result) {
    return result.isOk();
  }
  /**
   * Returns a curried function that will map the Ok value to another value. Will only run if the value is Ok.
   */
  static map(mapper) {
    return (r) => r.map(mapper);
  }
  /**
   * Returns a curried function that will map the Error value back to an OK value. Will only run if the value is Error.
   */
  static mapError(mapper) {
    return (r) => r.mapError(mapper);
  }
  /**
   * Returns a curried function that will bind the value to the result returned by the @param mapper function. Will only run if the value is Ok.
   */
  static bind(mapper) {
    return (r) => r.bind(mapper);
  }
  /**
   * Returns a curried function that will bind the result to the one returned by the @param mapper function. Will only run if the value is Error.
   */
  static bindError(mapper) {
    return (r) => r.bindError(mapper);
  }
  /**
   * Returns a curried function that will pass the value to the given @param fn function where it can then be used for work that doesn't return a value. Will only run if the value is Ok.
   */
  static iter(fn) {
    return (r) => r.iter(fn);
  }
  /**
   * Returns a curried function that will execute the given @param fn function when the result is Error.
   */
  static iterError(fn) {
    return (r) => r.iterError(fn);
  }
};

// src/async.ts
var Async = class _Async {
  constructor(value) {
    this.value = value;
  }
  /**
   * Gets the underlying promise, which can then be awaited.
   */
  get() {
    return this.value;
  }
  /**
   * Gets the underlying promise, wrapped in a Result. This is just a shortcut for `Result.ofPromise(Async.get())`
   */
  getResult() {
    return Result.ofPromise(this.value);
  }
  /**
   * Maps the value of the promise to another value.
   */
  map(mapper) {
    return _Async.ofPromise(this.value.then(mapper));
  }
  /**
   * Binds the value of the promise to the promise returned by the @param mapper function.
   */
  bind(mapper) {
    return _Async.ofPromise(this.value.then(mapper).then(_Async.get));
  }
  /**
   * Passes the value to the given @param fn function where it can then be used for work that doesn't require returning a value.
   */
  iter(fn) {
    this.value.then(fn);
    return this;
  }
  /**
   * Creates an Async monad from the promise.
   */
  static ofPromise(promise) {
    return new _Async(promise);
  }
  /**
   * Wraps a non-promise value in an Async monad.
   */
  static wrap(value) {
    return _Async.ofPromise(new Promise((res) => res(value)));
  }
  /**
   * Gets the underlying promise, which can then be awaited.
   */
  static get(a) {
    return a.get();
  }
  /**
   * Gets the underlying promise, wrapped in a Result. This is just a shortcut for `Result.ofPromise(Async.get())`
   */
  static getResult(a) {
    return a.getResult();
  }
  /**
   * Returns a curried function that will map the value of the promise to another value.
   */
  static map(mapper) {
    return (a) => a.map(mapper);
  }
  /**
   * Returns a curried function that will bind the value of the promise to the promise returned by the @param mapper function.
   */
  static bind(mapper) {
    return (a) => a.bind(mapper);
  }
  /**
   * Returns a curried function that will pass the value to the given @param fn function where it can then be used for work that doesn't require returning a value.
   */
  static iter(fn) {
    return (a) => a.iter(fn);
  }
};

// src/asyncresult.ts
var AsyncResult = class _AsyncResult {
  constructor(value) {
    this.value = value;
  }
  /**
   * Gets the underlying promise, which can then be awaited to return a @see Result
   */
  get() {
    return this.value;
  }
  /**
   * Maps the OK value to another value. Will only run if the value is OK.
   */
  map(fn) {
    return _AsyncResult.wrap(this.value.then(Result.map(fn)));
  }
  /**
   * Maps the Error value back to an OK value. Will only run if the value is Error.
   */
  mapError(fn) {
    return _AsyncResult.wrap(this.get().then(Result.mapError(fn)));
  }
  /**
   * Binds the OK value to the AsyncResult returned by the @param fn function. Will only run if the value is OK.
   */
  bind(fn) {
    return _AsyncResult.wrap(
      this.get().then((result) => __async(this, null, function* () {
        if (result.isOk()) {
          const value = fn(result.getValue());
          return value;
        }
        return _AsyncResult.wrap(Result.ofError(result.getError()));
      })).then(_AsyncResult.get)
    );
  }
  /**
   * Binds the Error value to the AsyncReuslt returned by the @param fn function. Will only run if the value is Error.
   */
  bindError(fn) {
    return _AsyncResult.wrap(
      this.get().then((result) => __async(this, null, function* () {
        if (result.isError()) {
          return fn(result.getError());
        }
        return _AsyncResult.wrap(result.getValue());
      })).then(_AsyncResult.get)
    );
  }
  /**
   * Passes the OK value to the given @param fn function. Will only run if the value is OK.
   */
  iter(fn) {
    this.get().then(Result.iter(fn));
    return this;
  }
  /**
   * Passes the Error value to the given @param fn function. Will only run if the value is Error.
   */
  iterError(fn) {
    this.get().then(Result.iterError(fn));
    return this;
  }
  /**
   * Wraps a value, promise or result in an AsyncResult monad.
   */
  static wrap(value) {
    if (value instanceof Result) {
      return new _AsyncResult(Promise.resolve(value));
    }
    if (value instanceof Promise) {
      return new _AsyncResult(
        value.then((value2) => value2 instanceof Result ? value2 : Result.ofValue(value2)).catch((ex) => Result.ofError(ex))
      );
    }
    return new _AsyncResult(Result.ofPromise(Promise.resolve(value)));
  }
  /**
   * Gets the underlying promise, which can then be awaited.
   */
  static get(a) {
    return a.get();
  }
  /**
   * Returns a curried function that will map the OK value to another value. Will only run if the value is OK.
   */
  static map(fn) {
    return (a) => a.map(fn);
  }
  /**
   * Returns a curried function that will map the Error value back to an OK value. Will only run if the value is Error.
   */
  static mapError(fn) {
    return (a) => a.mapError(fn);
  }
  /**
   * Returns a curried function that will bind the OK value to the AsyncResult returned by the @param fn function. Will only run if the value is OK.
   */
  static bind(fn) {
    return (a) => a.bind(fn);
  }
  /**
   * Returns a curried function that will bind the Error value to the AsyncResult returned by the @param fn function. Will only run if the value is Error.
   */
  static bindError(fn) {
    return (a) => a.bindError(fn);
  }
  /**
   * Returns a curried function that will pass the OK value to the given @param fn function. Will only run if the value is OK.
   */
  static iter(fn) {
    return (a) => a.iter(fn);
  }
  /**
   * Returns a curried function that will pass the Error value to the given @param fn function. Will only run if the value is Error.
   */
  static iterError(fn) {
    return (a) => a.iterError(fn);
  }
};

// src/compute.ts
function compute(block) {
  return block();
}

// src/option.ts
function _isSome(value) {
  return value !== void 0 && value !== null;
}
function _isNone(value) {
  return !_isSome(value);
}
var Option = class _Option {
  constructor(value) {
    this.value = value;
    /**
     * Checks whether the value is some.
     */
    this.isSome = () => _isSome(this.value);
    /**
     * Checks whether the value is none.
     */
    this.isNone = () => _isNone(this.value);
  }
  /**
   * Gets the underlying value. You *must* check whether the value is some or none before getting, as using this on a value that is none **will** throw an error.
   */
  get() {
    if (_isNone(this.value)) {
      throw new Error(
        "Attempted to get an option value that was none. Use Option.isSome to check if an option has a value before using it."
      );
    }
    return this.value;
  }
  /**
   * Maps the value to another value. Will only run if the value is some.
   */
  map(mapper) {
    if (_isSome(this.value)) {
      return _Option.ofSome(mapper(this.value));
    }
    return _Option.ofNone();
  }
  /**
   * Binds the value to another option. Will only run if the value is some.
   */
  bind(mapper) {
    if (_isSome(this.value)) {
      return mapper(this.value);
    }
    return _Option.ofNone();
  }
  /**
   * Passes the value to the given @param fn function where it can the be used for operations that don't require a value to be returned.
   */
  iter(fn) {
    if (_isSome(this.value)) {
      fn(this.value);
    }
    return this;
  }
  /**
   * Executes the given @param fn function when the the option has no value.
   */
  iterNone(fn) {
    if (_isNone(this.value)) {
      fn();
    }
    return this;
  }
  /**
   * Returns the @param defaultValue if the option is none, else returns the option's value.
   */
  defaultValue(defaultValue) {
    if (_isSome(this.value)) {
      return this.value;
    }
    return defaultValue;
  }
  /**
   * Executes the @param fn function if the value is none and returns the result, else returns the option value itself.
   */
  defaultWith(fn) {
    if (_isSome(this.value)) {
      return this.value;
    }
    return fn();
  }
  /**
   * Wraps the @param value in an option.
   */
  static ofSome(value) {
    if (_isNone(value)) {
      throw new Error("Option.some received a value that was null or undefined.");
    }
    return new _Option(value);
  }
  /**
   * Creates an option with no value.
   */
  static ofNone() {
    return new _Option();
  }
  /**
   * Checks whether the value is some.
   */
  static isSome(option) {
    return option.isSome();
  }
  /**
   * Checks whether the value is none.
   */
  static isNone(option) {
    return option.isNone();
  }
  /**
   * Gets the underlying value. You *must* check whether the value is some or none before getting, as using this on a value that is none **will** throw an error.
   */
  static get(option) {
    return option.get();
  }
  /**
   * Returns a curried function that will map the value to another value. Will only run if the value is some.
   */
  static map(mapper) {
    return (option) => option.map(mapper);
  }
  /**
   * Returns a curried function that will bind the value to another option. Will only run if the value is some.
   */
  static bind(mapper) {
    return (option) => option.bind(mapper);
  }
  /**
   * Returns a curried function that will pass the value to the given @param fn function where it can the be used for operations that don't require a value to be returned.
   */
  static iter(fn) {
    return (option) => option.iter(fn);
  }
  /**
   * Returns a curried function that executes the given @param fn function when the the option has no value.
   */
  static iterNone(fn) {
    return (option) => option.iterNone(fn);
  }
  /**
   * Returns a curried function that will return the @param defaultValue if the option is none, else returns the option's value.
   */
  static defaultValue(defaultValue) {
    return (option) => option.defaultValue(defaultValue);
  }
  /**
   * Returns a curried function that will execute the @param fn function if the value is none and returns the result, else returns the option's value itself.
   */
  static defaultWith(fn) {
    return (option) => option.defaultWith(fn);
  }
};

// src/pipe.ts
function pipe(val) {
  return {
    chain: (fn) => pipe(fn(val)),
    value: () => val
  };
}
export {
  Async,
  AsyncResult,
  Option,
  Result,
  WrappedResultError,
  compute,
  pipe
};
